import React, {Component} from 'react';
import {Button, Col, Form, Nav, Row, Tab} from 'react-bootstrap';
import {connect} from 'react-redux';
import Sidebar from '../../components/common/sidebar';
import Loader from '../../components/common/Loader';
import {notify} from '../../components/common/Toster';
import {trustScoreActions} from '../../_actions';

// The admin edits only the middle tier thresholds. The ends are fixed: a score of 0
// has no label, and scores at/above the highest tier are "Highly trusted".
const FIXED_TOP_LABEL = 'Highly trusted';

// Defaults used when the backend has no tiers saved yet (or the endpoint is unavailable).
const DEFAULT_TIERS = [
    {label: 'Verified member', score: 40},
    {label: 'Trusted profile', score: 80}
];

class TrustScore extends Component {
    state = {
        loader: true,
        isEdit: false,
        tiers: [],
        editTiers: [],
        tierErrors: [],
        errors: ''
    };

    componentDidMount() {
        this.getTrustScore();
    }

    getErrorMessage = (source, fallbackMessage) => {
        if (!source) {
            return fallbackMessage;
        }

        const payload = source.response && source.response.data
            ? source.response.data
            : source.data || source;

        if (payload.error && payload.error.responseMessage) {
            return payload.error.responseMessage;
        }

        if (payload.responseMessage) {
            return payload.responseMessage;
        }

        return source.message || fallbackMessage;
    };

    normalizeTiers = (list) => {
        if (!Array.isArray(list) || list.length === 0) {
            return DEFAULT_TIERS.map(tier => ({...tier}));
        }

        return list
            .map(tier => ({
                label: tier.label || '',
                score: Number(tier.score)
            }))
            .filter(tier => Number.isFinite(tier.score))
            .sort((a, b) => a.score - b.score);
    };

    getTrustScore = () => {
        this.setState({loader: true, errors: ''});

        this.props.dispatch(trustScoreActions.getTrustScore())
            .then(data => {
                if (data && data.statusCode === 1) {
                    const responseData = data.responseData || {};
                    const list = responseData.response || responseData.tiers || [];
                    const tiers = this.normalizeTiers(list);

                    this.setState({loader: false, tiers});
                    return;
                }

                // No data yet — fall back to defaults so the admin can still configure.
                this.setState({loader: false, tiers: this.normalizeTiers([])});
            })
            .catch(() => {
                // Endpoint unavailable (e.g. not deployed yet): fall back to defaults.
                this.setState({
                    loader: false,
                    tiers: this.normalizeTiers([]),
                    errors: ''
                });
            });
    };

    editMode = () => {
        const editTiers = this.state.tiers.map(tier => ({
            label: tier.label,
            score: String(tier.score)
        }));

        this.setState({
            isEdit: true,
            editTiers,
            tierErrors: editTiers.map(() => ({label: '', score: ''})),
            errors: ''
        });
    };

    cancelEdit = () => {
        this.setState({isEdit: false, editTiers: [], tierErrors: [], errors: ''});
    };

    handleTierChange = (index, field, value) => {
        const editTiers = this.state.editTiers.slice();
        editTiers[index] = {...editTiers[index], [field]: value};

        const tierErrors = this.state.tierErrors.slice();
        tierErrors[index] = {...tierErrors[index], [field]: ''};

        this.setState({editTiers, tierErrors, errors: ''});
    };

    validateTiers = () => {
        const {editTiers} = this.state;
        let hasError = false;

        // Labels are fixed (disabled) — only the score threshold is validated.
        const tierErrors = editTiers.map(tier => {
            const rowError = {label: '', score: ''};

            const score = Number(tier.score);
            if (tier.score === '' || tier.score === null || tier.score === undefined) {
                rowError.score = 'Score is required.';
                hasError = true;
            } else if (!Number.isFinite(score)) {
                rowError.score = 'Score must be a number.';
                hasError = true;
            } else if (score <= 0 || score > 100) {
                rowError.score = 'Score must be between 1 and 100.';
                hasError = true;
            }

            return rowError;
        });

        this.setState({tierErrors});

        return !hasError;
    };

    handleSubmit = (event) => {
        event.preventDefault();

        if (!this.validateTiers()) {
            return;
        }

        const tiers = this.state.editTiers.map(tier => ({
            label: tier.label.trim(),
            score: Number(tier.score)
        }));

        this.setState({loader: true, errors: ''});

        this.props.dispatch(trustScoreActions.saveTrustScore({tiers}))
            .then(data => {
                if (data && data.statusCode === 1) {
                    this.setState({isEdit: false, editTiers: [], tierErrors: []}, () => {
                        notify('success', 'Trust score tiers saved successfully.');
                        this.getTrustScore();
                    });
                    return;
                }

                const message = this.getErrorMessage(data, 'Unable to save trust score tiers.');
                this.setState({loader: false, errors: message});
                notify('error', message);
            })
            .catch(error => {
                const message = this.getErrorMessage(error, 'Unable to save trust score tiers.');
                this.setState({loader: false, errors: message});
                notify('error', message);
            });
    };

    // Score shown in the fixed top row (> X). In edit mode it tracks the last
    // editable input live; otherwise it uses the saved tiers.
    getTopThreshold() {
        const {isEdit, editTiers, tiers} = this.state;
        const source = isEdit ? editTiers : tiers;
        if (!source.length) {
            return 100;
        }
        const last = Number(source[source.length - 1].score);
        return Number.isFinite(last) ? last : 100;
    }

    renderTiersPane() {
        const {isEdit, tiers, editTiers, tierErrors, errors} = this.state;
        const source = isEdit ? editTiers : tiers;
        const topThreshold = this.getTopThreshold();

        // A tier's label applies to scores strictly below its score. Build the
        // display range for each editable tier from the one before it.
        let lower = 1;
        const tierRows = source.map((tier, index) => {
            const score = Number(tier.score);
            const upper = Number.isFinite(score) ? score - 1 : lower;
            const range = Number.isFinite(score) && upper >= lower ? `${lower} – ${upper}` : `${lower}`;
            if (Number.isFinite(score)) {
                lower = score;
            }
            return {label: tier.label, range, index};
        });

        return (
            <Form className="settings-form trust-score-form" onSubmit={this.handleSubmit} noValidate>
                <div className="trust-score-ranges">
                    <h4>Resulting ranges</h4>
                    <div className="trust-score-range-list">
                        {/* Fixed bottom: a score of 0 has no label. */}
                        <div className="trust-score-range-item">
                            <span className="trust-score-range-label">No label</span>
                            <strong className="trust-score-range-value">0</strong>
                        </div>

                        {/* Editable middle tiers: only the value turns into an input in edit mode. */}
                        {tierRows.map((row) => (
                            <div className="trust-score-range-item" key={`range-${row.index}`}>
                                <span className="trust-score-range-label">{row.label}</span>
                                {isEdit ? (
                                    <div className="trust-score-range-input">
                                        <Form.Control
                                            type="number"
                                            min="1"
                                            max="100"
                                            value={editTiers[row.index].score}
                                            placeholder="e.g. 40"
                                            onChange={e => this.handleTierChange(row.index, 'score', e.target.value)}
                                        />
                                        {tierErrors[row.index] && tierErrors[row.index].score && (
                                            <div className="settings-field-error">{tierErrors[row.index].score}</div>
                                        )}
                                    </div>
                                ) : (
                                    <strong className="trust-score-range-value">{row.range}</strong>
                                )}
                            </div>
                        ))}

                        {/* Fixed top: scores at/above the highest threshold. */}
                        <div className="trust-score-range-item">
                            <span className="trust-score-range-label">{FIXED_TOP_LABEL}</span>
                            <strong className="trust-score-range-value">{`> ${topThreshold}`}</strong>
                        </div>
                    </div>
                </div>

                {errors && <div className="error-message" role="alert">{errors}</div>}

                {isEdit && (
                    <div className="settings-form-actions">
                        <Button type="button" variant="light" onClick={this.cancelEdit}>Cancel</Button>
                        <Button type="submit" variant="info">Save changes</Button>
                    </div>
                )}
            </Form>
        );
    }

    render() {
        const {loader, isEdit} = this.state;

        return (
            <>
                <Sidebar/>
                <div className="body_container">
                    <div className="body_inner_wrapper">
                        {loader ? (
                            <Loader/>
                        ) : (
                            <>
                                <header className="header_content settings-page-header">
                                    <div>
                                        <p className="settings-page-title">Trust Score</p>
                                        <span>Manage the trust score status tiers shown to users.</span>
                                    </div>
                                </header>
                                <div className="main_content settings-main-content">
                                    <div className="setting_wrapper settings-card">
                                        <Tab.Container activeKey="Tiers">
                                            <Row noGutters className="settings-layout">
                                                <Col md={4} lg={3} className="settings-nav-column">
                                                    <div className="settings-nav-heading">Configuration</div>
                                                    <Nav variant="pills" className="flex-column settings-nav">
                                                        <Nav.Item>
                                                            <Nav.Link eventKey="Tiers">
                                                                <span className="settings-nav-icon">
                                                                    <i className="fas fa-shield-alt" aria-hidden="true"/>
                                                                </span>
                                                                <span className="settings-nav-copy">
                                                                    <strong>Score Tiers</strong>
                                                                    <small>Labels and thresholds</small>
                                                                </span>
                                                            </Nav.Link>
                                                        </Nav.Item>
                                                    </Nav>
                                                </Col>
                                                <Col md={8} lg={9} className="settings-content-column">
                                                    <Tab.Content className="settings-tab-content">
                                                        <Tab.Pane eventKey="Tiers">
                                                            <div className="settings-section-header">
                                                                <div>
                                                                    <h2>Trust score tiers</h2>
                                                                    <p>Set the score threshold for each tier.</p>
                                                                </div>
                                                                {!isEdit && (
                                                                    <Button type="button" variant="info" onClick={this.editMode}>
                                                                        <i className="fa fa-pencil" aria-hidden="true"/> Edit tiers
                                                                    </Button>
                                                                )}
                                                            </div>

                                                            {this.renderTiersPane()}
                                                        </Tab.Pane>
                                                    </Tab.Content>
                                                </Col>
                                            </Row>
                                        </Tab.Container>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </>
        );
    }
}

export default connect()(TrustScore);
